/**
 * /attendance/overtime/approvals + /me/attendance/overtime/approvals —
 * two-stage overtime approval queue (ADR-0014 D-4 v0.3). Mirrors leaveApprovals.
 *
 * Dual-mounted (same component): under the admin layout (HR roles) and the
 * employee layout (a non-HR employee who is someone's line manager — the
 * sticky-approver pattern; the API self-filters by approverUserId so a
 * non-approver simply sees an empty queue).
 *
 *   Tab 1 "Pending my approval"  — Stage 1, the manager queue
 *                                  (view=pending_my_approval; manager-approve).
 *   Tab 2 "Final approval"       — Stage 2, the HR-Admin queue
 *                                  (status=pending_hr_admin; approve = mints pay).
 *                                  Shown ONLY to owner/admin — `hr` cannot
 *                                  finalise (mirrors the API gate).
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrencyCode } from '@kora/types';
import { authedLayoutRoute } from './authed.js';
import { meLayoutRoute } from './meLayout.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  approveOvertime,
  listOvertime,
  managerApproveOvertime,
  rejectOvertime,
  type OvertimeItem,
} from '../api/overtime.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatDate, formatMoney } from '../lib/format.js';

export const overtimeApprovalsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/attendance/overtime/approvals',
  component: OvertimeApprovalsPage,
});

export const meOvertimeApprovalsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/attendance/overtime/approvals',
  component: OvertimeApprovalsPage,
});

type Tab = 'manager' | 'final';

function OvertimeApprovalsPage(): JSX.Element {
  const { user } = useAuth();
  const isHrAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [tab, setTab] = useState<Tab>('manager');

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Overtime approvals</h1>
        <p className="text-sm text-gray-400">
          Overtime is approved in two stages: the line manager confirms it was
          authorised, then an HR Admin gives the final sign-off that pays it.
        </p>
      </header>

      <nav className="flex gap-2 border-b border-gray-900">
        <TabButton active={tab === 'manager'} onClick={() => setTab('manager')}>
          Pending my approval
        </TabButton>
        {isHrAdmin ? (
          <TabButton active={tab === 'final'} onClick={() => setTab('final')}>
            Final approval
          </TabButton>
        ) : null}
      </nav>

      {tab === 'final' && isHrAdmin ? <FinalPanel /> : <ManagerPanel />}
    </section>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm border-b-2 ${
        active ? 'border-white text-white' : 'border-transparent text-gray-400 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

/** Stage 1 — the caller's manager queue. */
function ManagerPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['overtime', 'pending_my_approval'],
    queryFn: () => listOvertime({ view: 'pending_my_approval' }),
  });
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['overtime', 'pending_my_approval'] });

  if (query.isLoading) return <Placeholder>Loading…</Placeholder>;
  if (query.isError) return <Placeholder error>Could not load your queue.</Placeholder>;
  const items = query.data?.items ?? [];
  if (items.length === 0) return <Placeholder>Nothing pending your approval right now.</Placeholder>;
  return (
    <div className="space-y-4">
      {items.map((it) => (
        <OvertimeCard key={it.id} item={it} stage="manager" onDecided={() => void refresh()} />
      ))}
    </div>
  );
}

/** Stage 2 — HR Admin final-approval queue (owner/admin only). */
function FinalPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['overtime', 'pending_hr_admin'],
    queryFn: () => listOvertime({ status: 'pending_hr_admin' }),
  });
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['overtime', 'pending_hr_admin'] });

  if (query.isLoading) return <Placeholder>Loading…</Placeholder>;
  if (query.isError) return <Placeholder error>Could not load the final-approval queue.</Placeholder>;
  const items = query.data?.items ?? [];
  if (items.length === 0) return <Placeholder>Nothing awaiting final approval.</Placeholder>;
  return (
    <div className="space-y-4">
      {items.map((it) => (
        <OvertimeCard key={it.id} item={it} stage="final" onDecided={() => void refresh()} />
      ))}
    </div>
  );
}

function OvertimeCard({
  item,
  stage,
  onDecided,
}: {
  item: OvertimeItem;
  stage: Tab;
  onDecided: () => void;
}): JSX.Element {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onErr = (verb: string) => (err: unknown) =>
    setError(err instanceof ApiError ? err.message : `${verb} failed.`);

  const approveMut = useMutation({
    mutationFn: () => (stage === 'manager' ? managerApproveOvertime(item.id) : approveOvertime(item.id)),
    onSuccess: () => onDecided(),
    onError: onErr('Approval'),
  });
  const rejectMut = useMutation({
    mutationFn: () => rejectOvertime(item.id, { reason }),
    onSuccess: () => onDecided(),
    onError: onErr('Rejection'),
  });

  const name = item.employee
    ? `${item.employee.firstName} ${item.employee.lastName}`
    : item.employeeId;

  return (
    <div className="rounded border border-gray-900 p-4 space-y-3">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-white">
            {name}
            {item.employee ? (
              <span className="text-gray-500 font-mono text-xs ml-2">{item.employee.employeeNumber}</span>
            ) : null}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {formatDate(item.periodStart)} → {formatDate(item.periodEnd)} · {Number(item.overtimeHours)} OT hrs ·{' '}
            {Number(item.multiplier)}× rate
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-white font-mono">{formatMoney(item.computedAmount, item.currency as CurrencyCode)}</p>
          <p className="text-[0.65rem] uppercase tracking-wide text-gray-500">
            {stage === 'manager' ? 'manager approval' : 'final approval'}
          </p>
        </div>
      </header>

      {rejecting ? (
        <div className="space-y-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason (optional)"
            className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => rejectMut.mutate()}
              disabled={rejectMut.isPending}
              className="rounded bg-red-700 text-white text-xs px-3 py-1 disabled:opacity-50"
            >
              {rejectMut.isPending ? 'Rejecting…' : 'Confirm reject'}
            </button>
            <button
              type="button"
              onClick={() => {
                setRejecting(false);
                setReason('');
              }}
              className="rounded border border-gray-800 text-xs px-3 py-1 text-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => approveMut.mutate()}
            disabled={approveMut.isPending}
            className="rounded bg-green-700 text-white text-xs px-3 py-1 disabled:opacity-50"
          >
            {approveMut.isPending ? 'Approving…' : stage === 'manager' ? 'Approve' : 'Approve & pay'}
          </button>
          <button
            type="button"
            onClick={() => setRejecting(true)}
            className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900"
          >
            Reject
          </button>
        </div>
      )}
      {error ? <Alert variant="error" compact>{error}</Alert> : null}
    </div>
  );
}

function Placeholder({
  children,
  error,
}: {
  children: React.ReactNode;
  error?: boolean;
}): JSX.Element {
  return (
    <div
      className={`rounded border p-6 text-center text-sm ${
        error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'
      }`}
    >
      {children}
    </div>
  );
}
